// frontend/src/api/settingService.ts
import type { AxiosError } from 'axios'; // Added for potential error handling if needed later
import apiClient from "./index";
import type {
  SiteSetting, // Changed from Setting
  SettingUpdatePayload,
  PublicContactInfo,
  PublicAboutUsContentData,
  PublicGeneralSettings,
  // Assuming ArticleImageUploadResponse is defined in types, or create a generic one
  // If ImageUploadResponse is the actual name of the type for the response of uploadGeneralImage
  ImageUploadResponse, 
  UploadedImageFileListResponse
} from "@/types"; // Assuming @/types resolves to your types directory (e.g., ../types or ../types/index)
import axios from 'axios'; // Added for axios.isAxiosError check

// Define the structure for an available image (adjust as per your backend response)
export interface AvailableImage {
  image_url: string;
  filename: string;
  // Add other properties like id, upload_date, etc., if your backend provides them
}

/**
 * Fetches all available site settings from the backend.
 * Assumes the backend endpoint is /api/v1/settings/ (with trailing slash)
 * This is typically an admin-only function.
 * @returns A promise that resolves to an array of SiteSetting objects.
 */
export const getAllSettings = async (): Promise<SiteSetting[]> => {
  try {
    const response = await apiClient.get<SiteSetting[]>("/settings/");
    return response.data;
  } catch (error) {
    console.error('Error fetching all settings:', error);
    if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string | { msg: string; type: string }[] }>;
      if (apiError.response?.data?.detail) {
        if (typeof apiError.response.data.detail === 'string') throw new Error(apiError.response.data.detail);
        if (Array.isArray(apiError.response.data.detail)) throw new Error(apiError.response.data.detail.map(d => d.msg).join(', '));
      }
    }
    throw error;
  }
};

/**
 * Updates multiple site settings in bulk.
 * Uses the /api/v1/settings/bulk-update endpoint.
 * @param settingsData - An array of setting objects to update.
 * @returns A promise that resolves to an array of the updated SiteSetting objects.
 */
export const updateSettings = async (
  settingsData: SettingUpdatePayload[] // Assuming SettingUpdatePayload is compatible/defined in @/types
): Promise<SiteSetting[]> => {
  try {
    const payload = { settings: settingsData }; 
    const response = await apiClient.put<SiteSetting[]>(
      "/settings/bulk-update", 
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Error updating bulk settings:', error);
     if (axios.isAxiosError(error)) { 
      const apiError = error as AxiosError<{ detail?: any }>; 
      if (apiError.response && apiError.response.data && apiError.response.data.detail) {
        if (typeof apiError.response.data.detail === 'string') {
          throw new Error(apiError.response.data.detail);
        } else if (Array.isArray(apiError.response.data.detail)) {
          const messages = apiError.response.data.detail.map((err: any) => {
            if (err.loc && err.msg) { 
              return `${err.loc.join(' -> ')}: ${err.msg}`;
            }
            return JSON.stringify(err); 
          }).join('; ');
          throw new Error(`Validation errors: ${messages}`);
        } else if (typeof apiError.response.data.detail === 'object') {
           throw new Error(JSON.stringify(apiError.response.data.detail));
        }
      }
      throw new Error(apiError.message || 'An unknown API error occurred during bulk update.');
    } else {
      console.error('Non-Axios error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unknown error occurred during bulk update.');
    }
  }
};

// --- Specific Setting Uploads (Logo, Favicon) ---
export const uploadLogo = async (file: File): Promise<SiteSetting> => {
  try {
    const formData = new FormData();
    formData.append("logo_file", file); 
    const response = await apiClient.post<SiteSetting>(
      "/settings/upload-logo", // This endpoint needs to exist on the backend
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading logo:', error);
    if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string | { msg: string; type: string }[], message?: string, image_url?: string, filename?: string }>;
      if (apiError.response?.data?.detail) {
        if (typeof apiError.response.data.detail === 'string') throw new Error(apiError.response.data.detail);
      } else if (apiError.response?.data?.message) { // Check for alternative error structure
         throw new Error(apiError.response.data.message);
      }
    }
    throw error;
  }
};

export const uploadFavicon = async (file: File): Promise<SiteSetting> => {
  try {
    const formData = new FormData();
    formData.append("favicon_file", file);
    const response = await apiClient.post<SiteSetting>(
      "/settings/upload-favicon", // This endpoint needs to exist on the backend
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading favicon:', error);
    if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string | { msg: string; type: string }[], message?: string, image_url?: string, filename?: string }>;
      if (apiError.response?.data?.detail) {
        if (typeof apiError.response.data.detail === 'string') throw new Error(apiError.response.data.detail);
      } else if (apiError.response?.data?.message) {
         throw new Error(apiError.response.data.message);
      }
    }
    throw error;
  }
};


// --- General Image Upload Function ---
/**
 * Uploads a general image for site content.
 * Uses the /api/v1/uploads/image/general endpoint.
 * @param file - The image file to upload.
 * @returns A promise that resolves to an object containing the image URL.
 */
export const uploadGeneralImage = async (file: File): Promise<ImageUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file); // Matches the 'file: UploadFile = File(...)' parameter in backend

    // The endpoint is /uploads/image/general, so the path for apiClient is relative to /api/v1
    const response = await apiClient.post<ImageUploadResponse>(
      "/uploads/image/general", 
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data; // Expected: { message: string, image_url: string, filename?: string }
  } catch (error) {
    console.error('Error uploading general image:', error);
     if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string | { msg: string; type: string }[], message?: string, image_url?: string, filename?: string }>;
      if (apiError.response?.data?.detail) { // FastAPI validation error
        if (typeof apiError.response.data.detail === 'string') throw new Error(apiError.response.data.detail);
        if (Array.isArray(apiError.response.data.detail)) throw new Error(apiError.response.data.detail.map(d => d.msg).join(', '));
      } else if (apiError.response?.data?.message) { // Custom error message from endpoint
         throw new Error(apiError.response.data.message);
      }
    }
    throw error;
  }
};

// --- NEW FUNCTION: Get Available General Images ---
/**
 * Fetches a list of available general images.
 * Assumes an endpoint like /api/v1/uploads/list/general
 * @returns A promise that resolves to an array of AvailableImage objects.
 */
export const getAvailableGeneralImages = async (): Promise<UploadedImageFileListResponse> => {
  try {
    // Correct the endpoint from "/uploads/list/general" to "/uploads/image/general/available"
    const response = await apiClient.get<UploadedImageFileListResponse>("/uploads/image/general/available");
    // The backend returns an object like { files: [...] }, so we return the whole response data.
    return response.data;
  } catch (error) {
    console.error('Error fetching available general images:', error);
    if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string | { msg: string; type: string }[] }>;
      if (apiError.response?.data?.detail) {
        if (typeof apiError.response.data.detail === 'string') throw new Error(apiError.response.data.detail);
        if (Array.isArray(apiError.response.data.detail)) throw new Error(apiError.response.data.detail.map(d => d.msg).join(', '));
      }
    }
    // Return a default structure on error to prevent crashes in the component.
    return { files: [] };
  }
};


// --- Publicly Accessible Setting Getters ---
export const getPublicContactDetails = async (): Promise<PublicContactInfo> => {
  try {
    const response = await apiClient.get<PublicContactInfo>(
      "/settings/public-contact-info"
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching public contact details:', error);
    if (axios.isAxiosError(error)) {
      const apiError = error as AxiosError<{ detail?: string }>;
      if (apiError.response?.data?.detail) {
        throw new Error(apiError.response.data.detail);
      }
    }
    throw error;
  }
};

export const getPublicAboutUsContent =
  async (): Promise<PublicAboutUsContentData> => {
    try {
      const response = await apiClient.get<PublicAboutUsContentData>(
        "/settings/public-about-us"
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching public about us content:', error);
      if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) {
          throw new Error(apiError.response.data.detail);
        }
      }
      throw error;
    }
  };

export const getPublicGeneralSettings =
  async (): Promise<PublicGeneralSettings> => {
    try {
      const response = await apiClient.get<PublicGeneralSettings>(
        "/settings/public-general"
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching public general settings:', error);
      if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) {
          throw new Error(apiError.response.data.detail);
        }
      }
      throw error;
    }
  };

// Helper functions from the previous version that might still be needed
// depending on how SiteSettingsPage.tsx and other components consume settings.
// You may need to adapt or remove these if your new types (SiteSetting, etc.)
// and backend responses make them redundant.

// Example: If SiteSetting already has a direct value property and no complex value_type logic
// then getDisplayValue might not be needed or would be simpler.

// export const getDisplayValue = (setting?: SiteSetting | null): string | number | boolean | object | null => {
//   // This function would need to be adapted based on the structure of 'SiteSetting'
//   // and how values are stored (e.g., if it has value_str, value_int, etc., or a single 'value' field)
//   if (!setting) return null;
//   // Add logic here based on SiteSetting structure
//   // For example, if SiteSetting has a 'value' and 'value_type'
//   // switch (setting.value_type) { ... }
//   // return setting.value; // Or specific parsing if value is JSON string
//   console.warn("getDisplayValue needs implementation based on SiteSetting structure");
//   return (setting as any).value ?? null; // Placeholder
// };

// export const prepareSettingForUpdate = (
//   key: string, // Or a specific key enum if you have one for SiteSetting
//   value: any,
//   valueType: string, // Or a specific value type enum
//   currentSetting?: SiteSetting | null 
// ): SettingUpdatePayload => {
//   // This function would also need adaptation.
//   // SettingUpdatePayload should match what the backend /settings/bulk-update expects for each item.
//   console.warn("prepareSettingForUpdate needs implementation based on SiteSetting and SettingUpdatePayload structures");
//   return {
//     key,
//     // value, // if SettingUpdatePayload has a single 'value' field
//     // value_type: valueType, // if needed by backend
//     // Or, if it expects value_str, value_int etc.
//     ...(valueType === 'string' && { value_str: value }),
//     ...(valueType === 'integer' && { value_int: parseInt(value,10) }),
//     // ... and so on for other types
//   } as SettingUpdatePayload; // Placeholder
// };
